import dataLayerApi from "./dataLayerApi";
import windowFacade from "./window-facade";
import {
  dataAttributes,
  getAltText,
  getAriaLabel,
  getDataFromTargetOrThrow,
  getHrefFromTarget,
  getInvisibleInnerText,
  getVisibleInnerText,
  tryGetDataFromTarget,
} from "./get-data-from-entry";
import { closestElement } from "./closest-element";
import {
  closestDataSitAttribute,
  isHtmlElement,
} from "./closest-data-sit-attribute";
import { SearchLevel } from "./types";

const interactiveTags = ["a", "button"];

function clickHandler(event: MouseEvent) {
  const composedPath = event.composedPath();
  const navigationElement = closestElement(
    composedPath,
    "a[href], [data-sit-href], [data-sit-ignore-clicks]",
  );
  const location = closestDataSitAttribute(composedPath, "sitLocation");
  const variant = closestDataSitAttribute(composedPath, "sitVariant");
  const detail = closestDataSitAttribute(composedPath, "sitDetail");
  const elements = composedPath.filter((element) =>
    isHtmlElement(element),
  ) as HTMLElement[];
  if (navigationElement) {
    if (navigationElement.dataset.sitIgnoreClicks) {
      return;
    }

    handleNavigationClick(
      event,
      elements,
      navigationElement,
      location,
      variant,
      detail,
    );
  } else {
    const formWithAction = closestElement<HTMLFormElement>(
      composedPath,
      "form[action]",
    );
    const submitElement = closestElement(
      composedPath,
      'button[type="submit"], button:not([type]), input[type="submit"]',
    );
    if (formWithAction && submitElement) {
      handleFormSubmitClick(
        submitElement,
        formWithAction.action,
        location,
        variant,
        detail,
      );
    } else {
      handleGenericClick(elements, location, variant, detail);
    }
  }
}

function isSelectElement(element: any): element is HTMLSelectElement {
  return element.tagName?.toLowerCase() === "select";
}

function findUsefulLabelInChildren(element: HTMLElement) {
  if (isSelectElement(element)) {
    return element.options[element.selectedIndex]?.textContent?.trim();
  }
  if (element.dataset?.sitReplaceLabelWith !== undefined) {
    return element.dataset.sitReplaceLabelWith;
  }

  const visibleText =
    getVisibleInnerText(element) ||
    getAltText(element) ||
    getAriaLabel(element);
  if (
    interactiveTags.includes(element.tagName?.toLowerCase() ?? "") &&
    visibleText === ""
  ) {
    return getInvisibleInnerText(element);
  }
  return visibleText;
}

function findUsefulLabel([currentElement, ...parents]: HTMLElement[]): string {
  if (currentElement === document.body) {
    return "";
  }
  return (
    findUsefulLabelInChildren(currentElement) || findUsefulLabel(parents) || ""
  );
}

function handleGenericClick(
  composedPath: HTMLElement[],
  location?: string,
  variant?: string,
  detail?: string,
) {
  dataLayerApi.trackGenericClick({
    component: composedPath[0],
    label: findUsefulLabel(composedPath),
    location,
    variant,
    detail,
  });
}

function isInputElement(element: any): element is HTMLInputElement {
  return element.tagName.toLowerCase() === "input";
}

function handleFormSubmitClick(
  element: HTMLElement,
  destination: string,
  location?: string,
  variant?: string,
  detail?: string,
) {
  dataLayerApi.trackGenericNavigation({
    destination,
    component: element,
    label: isInputElement(element)
      ? element.value
      : (findUsefulLabelInChildren(element) ?? ""),
    location,
    variant,
    detail,
  });
}

function handleNavigationClick(
  event: MouseEvent,
  composedPath: HTMLElement[],
  element: HTMLElement,
  location?: string,
  variant?: string,
  detail?: string,
) {
  const destination = getHrefFromTarget(element) ?? "";
  const payload = {
    component: element,
    destination,
    location,
    variant,
    detail,
    callback: undefined as (() => void) | undefined,
  };

  if (
    !isExternalLink(destination ?? "") &&
    !isClickOpeningNewWindow(element, event) &&
    !event.defaultPrevented
  ) {
    payload.callback = () =>
      windowFacade.redirectTo(
        (element as HTMLAnchorElement).href ?? element.dataset.sitHref,
      );
    event.preventDefault();
  }

  const teaser = closestElement(composedPath, "[data-sit-teaser]");
  if (teaser) {
    dataLayerApi.trackTeaserNavigation({
      ...payload,
      label: tryGetDataFromTarget(teaser, dataAttributes.teaserLabel) ?? "",
      headline: getDataFromTargetOrThrow(
        teaser,
        dataAttributes.teaserHeadline,
      )!,
    });
    return;
  }

  if (isMarkedDownload(element)) {
    dataLayerApi.trackDownload({
      ...payload,
      label: getDataFromTargetOrThrow(element, dataAttributes.downloadLabel)!,
      filename: getDataFromTargetOrThrow(
        element,
        dataAttributes.downloadFilename,
      )!,
    });
    return;
  }

  if (isUnmarkedDownload(destination ?? "")) {
    dataLayerApi.trackDownload({
      ...payload,
      label: findUsefulLabelInChildren(element) ?? "",
      filename: (destination ?? "").match(/.*\/(.+?)$/)?.[1] ?? "",
    });
    return;
  }

  const searchResult = closestElement(composedPath, "[data-sit-search-result]");
  if (searchResult) {
    dataLayerApi.trackSearchResultNavigation({
      ...payload,
      label: getDataFromTargetOrThrow(
        searchResult,
        dataAttributes.searchResultLabel,
      )!,
      value: getDataFromTargetOrThrow(
        searchResult,
        dataAttributes.searchValue,
      )!,
      position: Number(
        getDataFromTargetOrThrow(
          searchResult,
          dataAttributes.searchResultPosition,
        ),
      ),
      page: Number(
        getDataFromTargetOrThrow(searchResult, dataAttributes.searchResultPage),
      ),
      level: getDataFromTargetOrThrow(
        searchResult,
        dataAttributes.searchResultLevel,
      ) as SearchLevel,
    });
    return;
  }

  if (isCustomNavigation(element)) {
    dataLayerApi.trackCustomNavigation({
      ...payload,
      label: findUsefulLabelInChildren(element) ?? "",
      action: tryGetDataFromTarget(element, dataAttributes.action),
      primaryCategory: tryGetDataFromTarget(
        element,
        dataAttributes.primaryCategory,
      ),
    });
    return;
  }

  dataLayerApi.trackGenericNavigation({
    ...payload,
    label: findUsefulLabelInChildren(element) ?? "",
  });
}

function isCustomNavigation(element: HTMLElement) {
  return (
    typeof element.dataset[dataAttributes.primaryCategory] === "string" ||
    typeof element.dataset[dataAttributes.action] === "string"
  );
}

function isMarkedDownload(element: HTMLElement) {
  return element.hasAttribute("data-sit-download");
}

function isUnmarkedDownload(destination: string) {
  return destination.match(/.*\/(.+?\.(?:pdf|docx?|xlsx?))$/);
}

function isExternalLink(url: string) {
  return url.startsWith("mailto:") || url.startsWith("tel:");
}

function isClickOpeningNewWindow(element: HTMLElement, event: MouseEvent) {
  return (
    element.getAttribute("target") === "_blank" ||
    event.ctrlKey ||
    event.shiftKey ||
    event.metaKey ||
    event.altKey ||
    event.button === 2
  );
}

export function registerGlobalClickTracker() {
  document.addEventListener("click", clickHandler);
}

export function unregisterGlobalClickTracker() {
  document.removeEventListener("click", clickHandler);
}
